import React from 'react';
import styled from '@emotion/styled';

const PaginationWrapper = styled.nav`
  display: flex;
  justify-content: flex-end;
  align-items: center;
  padding: 8px 16px;
  background-color: var(--body__background);
  color: var(--header__color);
`;

const PageButton = styled.button`
  background-color: ${props => props.selected ? 'var(--primary__color, #6860EE)' : 'var(--body__background)'};
  color: ${props => props.selected ? '#fff' : 'var(--header__color)'};
  border: 1px solid ${props => props.selected ? 'var(--primary__color, #6860EE)' : 'var(--border__color)'};
  border-radius: 4px;
  padding: 4px 8px;
  margin: 0 4px;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  opacity: ${props => props.disabled ? 0.5 : 1};
  font-size: 14px;
  
  &:hover:not(:disabled) {
    background-color: var(--pagination__hover, rgba(104, 96, 238, 0.1));
  }
  
  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

const PageInfoText = styled.span`
  margin: 0 8px;
  color: var(--header__color);
`;

const SelectWrapper = styled.div`
  margin: 0 8px;
`;

const RowsPerPageSelect = styled.select`
  background-color: var(--body__background);
  color: var(--header__color);
  border: 1px solid var(--border__color);
  border-radius: 8px;
  padding: 4px 8px;
  font-size: 14px;
  font-family: 'Satoshi', sans-serif;
`;

const CustomPagination = ({
  rowsPerPage,
  rowCount,
  onChangePage,
  onChangeRowsPerPage,
  currentPage,
  hideRowsPerPage = true,
}) => {
  const handleBackButtonClick = () => {
    onChangePage(currentPage - 1);
  };

  const handleNextButtonClick = () => {
    onChangePage(currentPage + 1);
  };

  const handleFirstPageButtonClick = () => {
    onChangePage(1);
  };

  const handleLastPageButtonClick = () => {
    onChangePage(Math.ceil(rowCount / rowsPerPage));
  };

  const lastPage = Math.ceil(rowCount / rowsPerPage);
  
  const range = (start, end) => {
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  };
  
  const getPageButtons = () => {
    if (lastPage <= 2) {
      return range(1, lastPage);
    }

    const endPage = Math.min(currentPage + 1, lastPage);

    if (endPage <= 5) {
      return range(1, endPage);
    }

    if (currentPage >= lastPage) {
      return range(lastPage - 4, lastPage);
    }

    return range(currentPage - 3, endPage);
  };

  return (
    <PaginationWrapper>
      {!hideRowsPerPage && (
        <>
          <PageInfoText>
            Rows per page:
          </PageInfoText>
          <SelectWrapper>
            <RowsPerPageSelect
              onChange={e => onChangeRowsPerPage(Number(e.target.value))}
              value={rowsPerPage}
            >
              <option value={10}>10</option>
              <option value={15}>15</option>
              <option value={20}>20</option>
              <option value={25}>25</option>
              <option value={30}>30</option>
            </RowsPerPageSelect>
          </SelectWrapper>
        </>
      )}
      
      <PageInfoText>
        {(currentPage - 1) * rowsPerPage + 1}-{Math.min(currentPage * rowsPerPage, rowCount)} of {rowCount}
      </PageInfoText>
      
      <PageButton 
        onClick={handleFirstPageButtonClick}
        disabled={currentPage === 1}
        title="First Page"
      >
        ⟪
      </PageButton>
      
      <PageButton 
        onClick={handleBackButtonClick} 
        disabled={currentPage === 1}
        title="Previous Page"
      >
        ⟨
      </PageButton>
      
      {getPageButtons().map(page => (
        <PageButton
          key={page}
          onClick={() => onChangePage(page)}
          selected={page === currentPage}
        >
          {page}
        </PageButton>
      ))}
      
      <PageButton 
        onClick={handleNextButtonClick} 
        disabled={currentPage === lastPage}
        title="Next Page"
      >
        ⟩
      </PageButton>
      
      <PageButton 
        onClick={handleLastPageButtonClick}
        disabled={currentPage === lastPage}
        title="Last Page"
      >
        ⟫
      </PageButton>
    </PaginationWrapper>
  );
};

export default CustomPagination;